import { Outlet, Navigate, useLocation } from "react-router-dom";
import type { Role } from "@etheria/shared";
import { useAuth } from "../../lib/auth";
import { fr } from "../../i18n/fr";
import { Loading, EmptyState, Button, OrnamentDefs } from "../ui";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { SideNav } from "./SideNav";
import { CLIENT_NAV, ADMIN_NAV } from "./navModel";
import { Link } from "react-router-dom";
import { WindowsNotificationPrompt } from "../system/WindowsNotificationPrompt";

/**
 * AppLayout — coquille globale : Header + <Outlet/> + Footer. Toutes les routes en héritent.
 */
export function AppLayout() {
  return (
    <div className="forge-app flex min-h-screen min-w-0 flex-col overflow-x-clip">
      <OrnamentDefs />
      <Header />
      <main className="content-layer min-w-0 flex-1">
        <Outlet />
      </main>
      <Footer />
      <WindowsNotificationPrompt />
    </div>
  );
}

/** Coquille du panel client : SideNav CLIENT + contenu. Redirige si non connecté. */
export function ClientLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <Loading className="py-24" />;
  if (!isAuthenticated) {
    return <Navigate to="/connexion" replace state={{ from: location.pathname }} />;
  }
  return (
    <div className="forge-workspace mx-auto grid w-full max-w-[1500px] gap-5 px-3 py-5 sm:px-5 sm:py-8 lg:grid-cols-[260px_minmax(0,1fr)]">
      <SideNav links={CLIENT_NAV} title={fr.nav.panel} />
      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

/** Coquille du panel admin : gate STAFF+. */
export function AdminLayout() {
  const { isAuthenticated, isStaff, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <Loading className="py-24" />;
  if (!isAuthenticated) {
    return <Navigate to="/connexion" replace state={{ from: location.pathname }} />;
  }
  if (!isStaff) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20">
        <EmptyState
          icon="⛔"
          title={fr.errors.accesRefuse}
          message={fr.errors.accesRefuseDesc}
          action={
            <Link to="/">
              <Button variant="secondary" size="sm">
                {fr.errors.retourAccueil}
              </Button>
            </Link>
          }
        />
      </div>
    );
  }
  return (
    <div className="forge-workspace mx-auto grid min-h-0 w-full max-w-[1500px] gap-5 px-3 py-5 sm:px-5 sm:py-8 lg:grid-cols-[260px_minmax(0,1fr)]">
      <SideNav links={ADMIN_NAV} title={fr.nav.admin} />
      <div className="min-h-0 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

/** Garde générique par rôle (utilisable ponctuellement). */
export function RequireRole({ min, children }: { min: Role; children: React.ReactNode }) {
  const { hasRole, isLoading } = useAuth();
  if (isLoading) return <Loading className="py-24" />;
  if (!hasRole(min)) return <Navigate to="/" replace />;
  return <>{children}</>;
}
